// Modules
import React, { useState, useCallback, useRef, useEffect } from "react"
import _ from "lodash"
import styled from "styled-components"
import Search from "@mui/icons-material/Search"

// Constants
import colors from "~constants/colors"

export interface TableSearchFieldProps {
  callback: Function
  searchBy: string[]
  placeholder: string
  searchOnType?: boolean
  showClearButton?: boolean
  showSearchIcon?: boolean
  initialValue?: string
  debounceMs?: number
}

const SearchWrapper = styled.div`
  align-items: center;
  display: flex;
`

const SearchInput = styled.input`
  border: none;
  color: ${colors.gray3};
  font-size: 14px;
  min-width: 210px;
  outline: none;
  padding-right: 25px;
`

const SearchIconStyle = {
  color: colors.blue1,
  fontSize: "26px",
  marginRight: "5px",
}

const ClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  padding: 5px;
  margin-left: -25px;
  width: 25px;
`

const sendQuery = (query: string, searchBy: string[], callback: Function) => {
  const props = searchBy.reduce((acc, val) => {
    acc[val] = acc[val] || query
    return acc
  }, {})
  callback(props)
}

const TableSearchField = ({
  searchBy,
  placeholder,
  callback,
  searchOnType = true,
  showClearButton = false,
  showSearchIcon = true,
  initialValue = "",
  debounceMs = 400,
}: TableSearchFieldProps) => {
  const [userQuery, setUserQuery] = useState(initialValue)
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  useEffect(() => {
    setUserQuery(initialValue)
  }, [initialValue])

  const delayedQuery = useCallback(
    _.debounce((query: string) => {
      sendQuery(query, searchBy, callbackRef.current)
    }, debounceMs),
    [searchBy, debounceMs],
  )

  useEffect(() => () => delayedQuery.cancel(), [delayedQuery])

  const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setUserQuery(e.target.value)
    if (searchOnType) {
      delayedQuery(e.target.value)
    }
  }

  const clear = () => {
    setUserQuery("")
    delayedQuery("")
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!searchOnType && e.key === "Enter") {
      delayedQuery(userQuery)
    }
  }

  return (
    <SearchWrapper>
      {showSearchIcon && <Search style={SearchIconStyle} />}
      <SearchInput
        type="text"
        placeholder={placeholder}
        onChange={inputChangeHandler}
        value={userQuery}
        onKeyPress={handleKeyPress}
      />
      {userQuery && showClearButton && (
        <ClearButton onClick={clear}>x</ClearButton>
      )}
    </SearchWrapper>
  )
}

export default TableSearchField
